<script lang="ts">
  /**
   * AsciiAnimation — canvas renderer for the ASCII animation engine.
   *
   * Renders the LayerCompositor output onto an HTML canvas using
   * dirty-cell-only redraws via DiffEngine. Supports three animation
   * levels: "prominent" (glyph rotation + border + particles + sprites),
   * "subtle" (border only, reduced opacity), and "off" (renders nothing).
   *
   * Context-aware effects: particles driven by narrative keywords,
   * sprites driven by character archetype, transitions between turns.
   */
  import {
    FrameBuffer,
    LayerKind,
    LayerCompositor,
    DiffEngine,
    GlyphConfig,
    GlyphRotator,
    Color,
    type CellDiff,
  } from "$lib/animation/engine";
  import {
    ParticleEmitter,
    rainTemplate,
    fireTemplate,
    magicTemplate,
    snowTemplate,
  } from "$lib/animation/particleEngine";
  import { createBuiltinAnimator, type SpriteAnimator } from "$lib/animation/spriteSheet";
  import { SceneTransition, TransitionType } from "$lib/animation/transitions";

  interface Props {
    width?: number;
    height?: number;
    animationLevel?: "prominent" | "subtle" | "off";
    glyphPercent?: number;
    glyphSpeedMs?: number;
    /** Narrative text — scanned for weather/effect keywords to drive particles. */
    narrativeContext?: string;
    /** Character archetype — drives sprite selection (warrior/mage). */
    characterArchetype?: string;
    /** Incremented each turn to trigger scene transitions. */
    turnCounter?: number;
  }

  let {
    width = 80,
    height = 24,
    animationLevel = "prominent",
    glyphPercent = 20,
    glyphSpeedMs = 200,
    narrativeContext = "",
    characterArchetype = "",
    turnCounter = 0,
  }: Props = $props();

  let canvas = $state<HTMLCanvasElement>(undefined!);
  let animFrameId: number;

  const CELL_WIDTH = 10;
  const CELL_HEIGHT = 18;
  const FONT = "14px 'Cascadia Code', 'Fira Code', 'Source Code Pro', monospace";

  let compositor: LayerCompositor;
  let rotator: GlyphRotator;
  let ctx: CanvasRenderingContext2D | null = null;

  // Particle system
  let particleEmitter: ParticleEmitter | null = null;
  let currentParticleEffect = "";
  let particleTickCounter = 0;

  // Sprite system
  let spriteAnimator: SpriteAnimator | null = null;
  let currentSpriteClip = "";

  // Scene transitions
  let transition: SceneTransition | null = null;
  let prevFrameSnapshot: FrameBuffer | null = null;
  let lastTurnCounter = 0;

  /** Keyword patterns that map narrative text to particle effects. */
  const PARTICLE_KEYWORDS: [string[], string][] = [
    [["rain", "storm", "downpour", "drizzle", "thunder", "wet", "soaked"], "rain"],
    [["fire", "flame", "burn", "torch", "inferno", "blaze", "ember"], "fire"],
    [["magic", "spell", "arcane", "enchant", "mystic", "rune", "glow", "shimmer"], "magic"],
    [["snow", "frost", "ice", "cold", "frozen", "blizzard", "chill", "winter"], "snow"],
  ];

  /** Detect particle effect from narrative text. Returns effect name or "". */
  function detectParticleEffect(text: string): string {
    const lower = text.toLowerCase();
    for (const [keywords, effect] of PARTICLE_KEYWORDS) {
      for (const kw of keywords) {
        if (lower.includes(kw)) return effect;
      }
    }
    return "";
  }

  /** Create a particle emitter for the given effect type. */
  function createEmitter(effect: string): ParticleEmitter | null {
    const seed = Date.now() & 0xffff;
    const spawnX: [number, number] = [1, width - 2];
    switch (effect) {
      case "rain":
        return new ParticleEmitter(rainTemplate(), 2.0, spawnX, [0, 0], seed);
      case "fire":
        return new ParticleEmitter(
          fireTemplate(),
          1.5,
          [width / 2 - 5, width / 2 + 5],
          [height - 2, height - 2],
          seed,
        );
      case "magic":
        return new ParticleEmitter(
          magicTemplate(),
          0.8,
          [width / 2 - 3, width / 2 + 3],
          [height / 2 - 2, height / 2 + 2],
          seed,
        );
      case "snow":
        return new ParticleEmitter(snowTemplate(), 1.0, spawnX, [0, 0], seed);
      default:
        return null;
    }
  }

  /** Select sprite clip based on character archetype. */
  function archetypeToClip(archetype: string): string {
    const lower = archetype.toLowerCase();
    if (
      lower.includes("mage") ||
      lower.includes("wizard") ||
      lower.includes("sorcerer") ||
      lower.includes("witch")
    ) {
      return "mage_idle";
    }
    return "warrior_idle";
  }

  function initEngine(): void {
    compositor = new LayerCompositor(width, height);
    compositor.addLayer(LayerKind.Border, width, height);
    compositor.addLayer(LayerKind.Sprite, width, height);
    compositor.addLayer(LayerKind.Glyph, width, height);

    const config = new GlyphConfig();
    config.rotationPercent = glyphPercent;
    config.rotationWindowMs = glyphSpeedMs;
    rotator = new GlyphRotator(width, height, config);

    const borderLayer = compositor.layerMut(LayerKind.Border);
    if (borderLayer) {
      drawBorder(borderLayer.buffer);
    }

    // Initialize sprite animator with all built-in clips
    spriteAnimator = createBuiltinAnimator();
    spriteAnimator.setPosition(2, height - 5);
  }

  function drawBorder(buf: FrameBuffer): void {
    const [w, h] = buf.dimensions();
    const borderColor = new Color(30, 120, 30);
    const bgColor = Color.black();
    for (let c = 0; c < w; c++) {
      buf.set(0, c, "~", borderColor, bgColor);
      buf.set(h - 1, c, "~", borderColor, bgColor);
    }
    for (let r = 0; r < h; r++) {
      buf.set(r, 0, "|", borderColor, bgColor);
      buf.set(r, w - 1, "|", borderColor, bgColor);
    }
  }

  function renderFrame(now: number): void {
    if (!canvas || animationLevel === "off" || !ctx) return;

    if (animationLevel === "prominent") {
      // Tick glyph rotation
      const glyphLayer = compositor.layerMut(LayerKind.Glyph);
      if (glyphLayer) {
        rotator.tick(now, glyphLayer.buffer);
      }

      // Tick particles (every 2 frames for performance)
      particleTickCounter++;
      if (particleEmitter && particleTickCounter % 2 === 0) {
        particleEmitter.update();
        const spriteLayer = compositor.layerMut(LayerKind.Sprite);
        if (spriteLayer) {
          // Clear sprite layer before re-rendering particles and sprites
          spriteLayer.buffer.clear();
          particleEmitter.render(spriteLayer.buffer);
        }
      }

      // Tick and render sprite
      if (spriteAnimator) {
        spriteAnimator.update();
        const spriteLayer = compositor.layerMut(LayerKind.Sprite);
        if (spriteLayer) {
          spriteAnimator.render(spriteLayer.buffer);
        }
      }
    }

    // Composite all visible layers
    compositor.composite();

    // Apply scene transition if active
    let outputBuf = compositor.output();
    if (transition && !transition.isComplete()) {
      transition.advance();
      if (prevFrameSnapshot) {
        outputBuf = transition.apply(prevFrameSnapshot, outputBuf);
      }
      if (transition.isComplete()) {
        transition = null;
        prevFrameSnapshot = null;
      }
    }

    // Get only dirty-cell diffs (not full-screen)
    const diffs = DiffEngine.compute(outputBuf);

    if (diffs.length > 0) {
      // --- Pass 1: background fills, batched by bg color ---
      const bgGroups = new Map<string, CellDiff[]>();
      for (const diff of diffs) {
        const key = `${diff.bg.r},${diff.bg.g},${diff.bg.b}`;
        let group = bgGroups.get(key);
        if (!group) {
          group = [];
          bgGroups.set(key, group);
        }
        group.push(diff);
      }
      for (const [color, group] of bgGroups) {
        ctx.fillStyle = `rgb(${color})`;
        for (const diff of group) {
          ctx.fillRect(diff.col * CELL_WIDTH, diff.row * CELL_HEIGHT, CELL_WIDTH, CELL_HEIGHT);
        }
      }

      // --- Pass 2: foreground text, batched by fg color ---
      const fgGroups = new Map<string, CellDiff[]>();
      for (const diff of diffs) {
        const key = `${diff.fg.r},${diff.fg.g},${diff.fg.b}`;
        let group = fgGroups.get(key);
        if (!group) {
          group = [];
          fgGroups.set(key, group);
        }
        group.push(diff);
      }
      for (const [color, group] of fgGroups) {
        ctx.fillStyle = `rgb(${color})`;
        for (const diff of group) {
          ctx.fillText(diff.ch, diff.col * CELL_WIDTH + 1, diff.row * CELL_HEIGHT + 1);
        }
      }

      // Clear dirty flags after rendering
      if (outputBuf === compositor.output()) {
        outputBuf.clearDirty();
      }
    }

    animFrameId = requestAnimationFrame(renderFrame);
  }

  // Initialize engine and start render loop when canvas is available
  $effect(() => {
    if (canvas && animationLevel !== "off") {
      initEngine();
      canvas.width = width * CELL_WIDTH;
      canvas.height = height * CELL_HEIGHT;
      ctx = canvas.getContext("2d", { alpha: false });
      if (ctx) {
        ctx.font = FONT;
        ctx.textBaseline = "top";
        ctx.textRendering = "optimizeSpeed";
      }
      animFrameId = requestAnimationFrame(renderFrame);
    }

    return () => {
      if (animFrameId) cancelAnimationFrame(animFrameId);
      ctx = null;
    };
  });

  // Update rotator config when glyph settings change
  $effect(() => {
    if (rotator) {
      const config = new GlyphConfig();
      config.rotationPercent = glyphPercent;
      config.rotationWindowMs = glyphSpeedMs;
      rotator.setConfig(config);
    }
  });

  // React to narrative context changes — update particle effect
  $effect(() => {
    if (animationLevel !== "prominent") {
      if (particleEmitter) {
        particleEmitter.setActive(false);
        particleEmitter = null;
        currentParticleEffect = "";
      }
      return;
    }
    const effect = detectParticleEffect(narrativeContext);
    if (effect !== currentParticleEffect) {
      currentParticleEffect = effect;
      particleEmitter = effect ? createEmitter(effect) : null;
    }
  });

  // React to character archetype changes — update sprite
  $effect(() => {
    if (animationLevel !== "prominent" || !spriteAnimator) return;
    const clip = characterArchetype ? archetypeToClip(characterArchetype) : "warrior_idle";
    if (clip !== currentSpriteClip) {
      currentSpriteClip = clip;
      spriteAnimator.play(clip);
    }
  });

  // React to turn changes — trigger dissolve transition
  $effect(() => {
    if (animationLevel === "off" || !compositor) return;
    if (turnCounter > lastTurnCounter && lastTurnCounter > 0) {
      // Snapshot current frame before new content arrives
      prevFrameSnapshot = compositor.output().clone();
      transition = new SceneTransition(TransitionType.Dissolve, 20, width, height);
    }
    lastTurnCounter = turnCounter;
  });
</script>

{#if animationLevel !== "off"}
  <canvas
    bind:this={canvas}
    class="ascii-animation"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 0; pointer-events: none; opacity: {animationLevel ===
    'subtle'
      ? 0.4
      : 0.7}; image-rendering: pixelated;"
  ></canvas>
{/if}
